import React, { useCallback, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, ICellRendererParams, IHeaderParams } from 'ag-grid-community'
import { agConfigSideBar, tableCellStyle } from '../../../utils/AgGridConfig'
import { KTSVG } from '../../../_metronic/helpers/components/KTSVG'
import { Padding } from '@mui/icons-material'
interface ChartDataType {
  label: string
  data: number[]
  backgroundColor?: string | CanvasPattern
  borderColor?: string | CanvasPattern
  borderDash?: number[]
  type?: 'line' | 'bar'
  options?: any
  yAxisID?: string
}

interface ChartDataTableProps {
  labels: string[]
  chartData: ChartDataType[]
  groupName?: string
  title?: string
  onExport?: () => void
  onRefresh?: () => void
}

interface ProcessedRowData {
  id: string
  itemName: string
  [key: string]: any
}

const truncateTo2Decimals = (val: number) => {
  return Math.floor(val * 100) / 100
}

const TwoLineValueRenderer = (params: any) => {
  const { value } = params

  if (!value || (!value.projected && !value.actual)) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: '100%',
          textAlign: 'left',
          padding: '6',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            fontWeight: '400',
            padding: '6px',
            borderRadius: '2px',
            minHeight: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          N/A
        </span>
      </div>
    )
  }

  const formatValue = (val: number | undefined) => {
    if (val === undefined || val === null) return 'N/A'

    if (
      params.data?.yAxisID === 'y1' ||
      (typeof val === 'number' && val <= 1 && val > 0 && val.toString().includes('.'))
    ) {
      return val == null
        ? 'N/A'
        : Number.isInteger(val)
        ? `${val}%`
        : `${truncateTo2Decimals(val)}%`
    }

    return Number.isInteger(val) ? val.toLocaleString('en-IN') : truncateTo2Decimals(val)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'left',
        height: '100%',
        textAlign: 'left',
        padding: '6px ',
      }}
    >
      <div
        style={{
          fontSize: '12px',
          fontWeight: '400',
          marginBottom: '2px',
          padding: '6 px',
          borderRadius: '2px',
          minHeight: '16px',
          display: 'flex',
          alignItems: 'left',
          justifyContent: 'flex-start',
        }}
      >
        {formatValue(value.projected)}
      </div>
      <div
        style={{
          fontSize: '12px',
          fontWeight: '600',
          padding: '6 px',
          borderRadius: '2px',
          minHeight: '16px',
          display: 'flex',
          alignItems: 'left',
          justifyContent: 'flex-start',
        }}
      >
        {formatValue(value.actual)}
      </div>
    </div>
  )
}

const TimelineHeaderRenderer = (params: IHeaderParams) => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px',
        boxSizing: 'border-box',
      }}
      className='custom-header'
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '13px',
            color: '#4a4e52ff',
            marginBottom: '4px',
          }}
        >
          {params.displayName}
        </div>
        <div
          style={{
            fontSize: '10px',
            color: '#6c757d',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            lineHeight: '14px',
          }}
        >
          <span>Projected</span>
          <span style={{ color: '#212529' }}>Actual</span>
        </div>
      </div>

      <span
        className='ag-icon ag-icon-menu header-menu-icon'
        onClick={(e) => params.showColumnMenu(e.currentTarget)}
        style={{
          cursor: 'pointer',
          fontSize: '18px',
          color: '#6c757d',
        }}
      ></span>
    </div>
  )
}

const ChartDataTable: React.FC<ChartDataTableProps> = ({
  labels,
  chartData,
  groupName,
  //   title = " ",
  onExport,
  onRefresh,
}) => {
  const gridApi = useRef<any>(null)
  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const processedData = useMemo(() => {
    const groupedData: { [key: string]: { actual?: ChartDataType; projected?: ChartDataType } } = {}

    chartData.forEach((item) => {
      let baseLabel = item.label
      let type: 'actual' | 'projected' = 'actual'

      if (item.label.includes('(Actual)')) {
        baseLabel = item.label.replace(/\s*\(Actual\)\s*/g, '').trim()
        type = 'actual'
      } else if (item.label.includes('(Projected)')) {
        baseLabel = item.label.replace(/\s*\(Projected\)\s*/g, '').trim()
        type = 'projected'
      }

      if (!groupedData[baseLabel]) {
        groupedData[baseLabel] = {}
      }

      groupedData[baseLabel][type] = item
    })

    const tableData: ProcessedRowData[] = []

    Object.entries(groupedData).forEach(([itemName, group]) => {
      const row: ProcessedRowData = {
        id: itemName.replace(/\s+/g, '_').toLowerCase(),
        itemName: itemName,
        yAxisID: group.actual?.yAxisID || group.projected?.yAxisID || 'y',
      }

      labels.forEach((label, idx) => {
        const actualValue = group.actual?.data[idx]
        const projectedValue = group.projected?.data[idx]

        row[label] = {
          actual: actualValue,
          projected: projectedValue,
        }
      })

      tableData.push(row)
    })

    return tableData
  }, [chartData, labels])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filter: true,
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      minWidth: 150,
      resizable: true,
      flex: 1,
    }),
    []
  )

  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        headerName: 'Group Name | Items',
        field: 'itemName',
        pinned: 'left',
        flex: 2,
        minWidth: 250,
        maxWidth: 350,

        cellStyle: {
          ...tableCellStyle,
          backgroundColor: '#f8f9fa',
          borderRight: '2px solid #dee2e6',
          fontWeight: '500',
          padding: '6',
          display: 'flex',
          alignItems: 'center',
          Padding: '6px',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
        tooltipField: 'itemName',
        sortable: true,
        menuTabs: ['filterMenuTab'],
        getQuickFilterText: (params) => params.value || '',
      },
    ]

    const validLabels = labels.filter(
      (label) => label && typeof label === 'string' && label.trim() !== ''
    )
    validLabels.forEach((label) => {
      cols.push({
        headerName: label,
        field: label,
        minWidth: 200,
        maxWidth: 250,
        cellRenderer: TwoLineValueRenderer,
        headerComponent: TimelineHeaderRenderer,
        cellStyle: {
          ...tableCellStyle,
          padding: '6',
          borderRight: '1px solid #dee2e6',
        },
        getQuickFilterText: () => '',
        comparator: (valueA, valueB) => {
          const projA = valueA?.projected ?? 0
          const projB = valueB?.projected ?? 0
          return projA - projB
        },
        filter: 'agMultiColumnFilter',
        filterParams: {
          maxNumConditions: 5,
          buttons: ['reset'],
          filters: [
            {
              title: 'Projected',
              display: 'subMenu',
              filter: 'agTextColumnFilter',
              buttons: ['reset'],
              filterValueGetter: (params: any) => params.data[label]['projected'] ?? null,
              filterParams: {
                buttons: ['reset'],
                valueGetter: (params: any) => params.data[label]['projected'] ?? null,
              },
            },
            {
              title: 'Actual',
              display: 'subMenu',
              filter: 'agTextColumnFilter',
              buttons: ['reset'],
              filterValueGetter: (params: any) => params.data[label]['actual'] ?? null,
              filterParams: {
                buttons: ['reset'],
                valueGetter: (params: any) => params.data[label]['actual'] ?? null,
              },
            },
          ],
        },
        tooltipValueGetter: (params) => {
          if (!params.value) return 'N/A'

          const formatNumber = (num: number | null | undefined) => {
            if (num == null || isNaN(num)) return 'N/A'
            if (Number.isInteger(num)) {
              return num.toLocaleString('en-IN')
            }
            return truncateTo2Decimals(num)
          }

          const projected =
            params.value.projected != null && params.value.projected !== 0
              ? `Projected: ${formatNumber(params.value.projected)}`
              : 'Projected: N/A'

          const actual =
            params.value.actual != null && params.value.actual !== 0
              ? `Actual: ${formatNumber(params.value.actual)}`
              : 'Actual: N/A'

          return [projected, actual].filter(Boolean).join('\n')
        },
        sortable: true,
        menuTabs: ['filterMenuTab'],
      })
    })
    return cols
  }, [labels])
  const rowData = useMemo(() => processedData ?? [], [processedData])

  const openToolPanel = (key: 'columns' | 'filters') => {
    const isOpen = key === 'columns' ? isColumnOpen : isFilterOpen
    const toggle = !isOpen
    gridApi.current?.api?.[toggle ? 'openToolPanel' : 'closeToolPanel'](key)
    setIsColumnOpen(key === 'columns' ? toggle : false)
    setIsFilterOpen(key === 'filters' ? toggle : false)
  }

  return (
    <div
      className='card p-0 h-450px border-0 '
      style={{ boxShadow: '0px 3px 4px rgba(0,0,0,0.03)', border: '1px solid #F1F1F2' }}
    >
      {/* Header */}
      <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 p-3'>
        <div className='card-title'>{/* Optional title */}</div>

        {/* Controls */}
        <div className='d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 w-100 w-md-auto'>
          {/* Search */}
          <span
            className='d-flex align-items-center border border-1 border-secondary-txt bg-transparent h-38px rounded flex-grow-1'
            style={{
              boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.03)',
              borderColor: '#F1F1F2',
              zIndex: 6,
            }}
          >
            <KTSVG
              path={'media/icons/duotune/general/gen021.svg'}
              className='ps-3 svg-icon-2x text-start fs-9'
              svgClassName='text-gray-700'
            />
            <input
              type='search'
              placeholder='Search by Item Name'
              className='form-control border-0 h-35px rounded w-100'
              onChange={(e) => {
                gridApi.current.api.setQuickFilter(e.target.value)
              }}
            />
          </span>

          {/* Columns Button */}
          <button
            onClick={() => openToolPanel('columns')}
            className='h-38px px-4 rounded border border-1 border-secondary-txt bg-white d-flex align-items-center justify-content-center gap-1 text-gray-700 fs-7 fw-normal w-100 w-sm-auto'
          >
            <KTSVG
              path='/media/icons/duotune/layouts/lay004.svg'
              className='svg-icon-3 text-secondary-txt'
            />
            Columns
          </button>

          {/* Filter Button */}
          <button
            onClick={() => openToolPanel('filters')}
            className='h-38px px-4 rounded border border-1 border-secondary-txt bg-white d-flex align-items-center justify-content-center gap-1 text-gray-700 fs-7 fw-normal w-100 w-sm-auto'
          >
            <KTSVG
              path='/media/icons/duotune/general/gen031.svg'
              className='svg-icon-3 text-secondary-txt'
            />
            Filter
          </button>
        </div>
      </div>

      {/* Body */}
      <div className='card-body w-100 p-6 pt-0 overflow-auto ag-theme-material h-100'>
        <AgGridReact
          ref={gridApi}
          sideBar={agConfigSideBar}
          defaultColDef={defaultColDef}
          domLayout='normal'
          alwaysShowHorizontalScroll
          alwaysMultiSort
          animateRows={true}
          columnDefs={columnDefs}
          rowData={rowData}
          pagination={false}
          enableCharts={true}
          enableRangeSelection={true}
          suppressRowTransform={true}
          suppressCopyRowsToClipboard={true}
          suppressCopySingleCellRanges={true}
          suppressCellFocus={true}
          suppressMenuHide={false}
          tooltipShowDelay={50}
          tooltipInteraction={true}
          rowClass={'border-right-0 border-left-0 border-dashed'}
          overlayNoRowsTemplate={
            '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Chart Data To Show</span>'
          }
          getRowStyle={(params) => ({
            background: params.rowIndex % 2 === 0 ? '#f8f8f8' : '#ffffff',
          })}
          onGridReady={(params) => {
            params.api.sizeColumnsToFit()
          }}
          onGridSizeChanged={(params) => {
            params.api.sizeColumnsToFit()
          }}
        />
      </div>
    </div>
  )
}
export default ChartDataTable
